/**
 * Internal QA license-override contract (EDUS-2726). The platform console's
 * QA page seeds the Redis license cache for a fixed set of INTERNAL TEST
 * domains so QA can drive every license-dependent login verdict
 * (APP-eligible / NOT_LICENSED / PROSPECT / UNAVAILABLE) without a real Chargify
 * subscription. Shared so the console dropdowns and the backend validator
 * can't drift. UNAVAILABLE is special — see `simulatesUnavailable`.
 *
 * SAFETY: this can fabricate app access, so the backend hard-restricts it to
 * QA_OVERRIDE_TEST_DOMAINS — never a real customer. The override is written
 * with a short TTL and self-heals to the real Chargify state on expiry.
 */
import type { LicenseTier } from './license.model';

/**
 * The ONLY domains a QA override may target. Internal test/demo tenants, not
 * real customers. A tripwire test asserts this set is exactly these three, so
 * adding a real domain is a loud, reviewed change — not a quiet one-liner.
 */
export const QA_OVERRIDE_TEST_DOMAINS: readonly string[] = [
  'gafetest.com',
  'anexampleschool.com',
  'gedu.demo.amplifiedit.com',
];

/**
 * How long every QA override lives before it self-heals to the real state. One
 * value for all the QA levers, so the console's "~1 hour" copy is true of each.
 */
export const QA_OVERRIDE_TTL_SECONDS = 3600; // 1h

/**
 * A QA license scenario, fully self-describing: the console reads `key`/`label`
 * for the dropdown, the backend reads `inChargify`/`tier` to build the facts a
 * real Chargify read would produce. Single source — adding a scenario is one
 * edit here, and both sides pick it up.
 */
export interface LicenseQaScenarioOption {
  readonly key: string;
  readonly label: string;
  /** The license state to seed: false = the "not a Chargify customer" answer. */
  readonly inChargify: boolean;
  /** Tier for the seeded facts. Ignored (placeholder) when inChargify is false. */
  readonly tier: LicenseTier;
  /**
   * Simulate a fail-closed upstream outage instead of seeding facts. The backend
   * writes a marker the license check THROWS on (LicenseCheckError), so the login
   * router resolves the real `UNAVAILABLE` verdict — the one verdict that comes
   * from a thrown error, not a business fact, and so has no facts to seed.
   */
  readonly simulatesUnavailable?: boolean;
}

export const LICENSE_QA_SCENARIOS = [
  { key: 'paid', label: 'Paid → App access', inChargify: true, tier: 'Paid' },
  { key: 'trial', label: 'Trial → App access', inChargify: true, tier: 'Trial' },
  { key: 'consulting', label: 'Consulting → App access', inChargify: true, tier: 'Consulting' },
  { key: 'lapsed', label: 'Lapsed → Not licensed, renew copy', inChargify: true, tier: 'Lapsed' },
  { key: 'free', label: 'Free → Not licensed, upgrade copy', inChargify: true, tier: 'Free' },
  { key: 'unlicensed', label: 'Unlicensed → Not licensed', inChargify: true, tier: 'Unlicensed' },
  { key: 'not_in_chargify', label: 'Not in Chargify → Contact support', inChargify: false, tier: 'Unlicensed' },
  { key: 'unavailable', label: 'Unavailable → Upstream error, fail closed', inChargify: false, tier: 'Unlicensed', simulatesUnavailable: true },
] as const satisfies readonly LicenseQaScenarioOption[];

export type LicenseQaScenario = (typeof LICENSE_QA_SCENARIOS)[number]['key'];

export const LICENSE_QA_SCENARIO_KEYS: readonly LicenseQaScenario[] =
  LICENSE_QA_SCENARIOS.map((s) => s.key);

/**
 * Does a scenario simulate a fail-closed upstream outage (→ UNAVAILABLE) rather
 * than seeding facts? A helper because `as const` narrows each element so the
 * optional flag isn't on the union — callers shouldn't have to cast.
 */
export function scenarioSimulatesUnavailable(
  key: LicenseQaScenario
): boolean {
  return (
    (LICENSE_QA_SCENARIOS as readonly LicenseQaScenarioOption[]).find(
      (s) => s.key === key
    )?.simulatesUnavailable === true
  );
}
